import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
    getAllCashBoxes,
    createCashBox,
    openTodayCashBox,
    closeCashBox,
    deleteCashBox,
    getCashBoxUsers
} from "services/cashBoxService";
import {

    CashBox,
    CashBoxCreatePayload,
    CashBoxOpenTodayPayload,
    CashBoxClosePayload,
    CashBoxSearchParams
} from "types/cashBoxTypes";
import { UserBase } from "types/userTypes";

export const useCashBox = () => {
    const { t } = useTranslation();

    const [cashBoxes, setCashBoxes] = useState<CashBox[]>([]);
    const [pagination, setPagination] = useState<any>({
        page: 1,
        limit: 10,
        total: 0,
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [users, setUsers] = useState<UserBase[]>([]);

    // UI State
    const [selectedCashBox, setSelectedCashBox] = useState<CashBox | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);


    const fetchCashBoxes = useCallback(
        async (params: CashBoxSearchParams = {}) => {
            setLoading(true);
            try {
                const response = await getAllCashBoxes(params);
                setCashBoxes(response.data);
                setPagination(response.pagination);
                setError(null);
            } catch (err) {
                console.error(err);
                setCashBoxes([]);
                const msg = t("errors.ERROR_FETCHING_DATA");
                setError(msg);
            } finally {
                setLoading(false);
            }
        },
        [t]
    );

    const fetchUsers = useCallback(async () => {
        try {
            const res = await getCashBoxUsers();
            setUsers(res.data);
        } catch (error) {
            console.error(error);
            message.error("Failed to load users");
        }
    }, []);


    const handleCreate = async (payload: CashBoxCreatePayload) => {
        try {
            await createCashBox(payload);
            message.success(t("cashbox.success.createD"));
            await fetchCashBoxes();
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    };


    const handleOpenToday = async (payload: CashBoxOpenTodayPayload = {}) => {
        try {
            await openTodayCashBox(payload);
            message.success(t("cashbox.success.opened"));
            await fetchCashBoxes();
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    };


    const handleClose = async (
        cashBoxId: string,
        payload: CashBoxClosePayload
    ) => {
        try {
            await closeCashBox(cashBoxId, payload);
            message.success(t("cashbox.success.closed"));
            await fetchCashBoxes();
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    };


    const handleDelete = async (cashBoxId: string) => {
        try {
            await deleteCashBox(cashBoxId);
            message.success(t("cashbox.success.deleted"));
            await fetchCashBoxes();
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    };

    return {
        cashBoxes,
        infoPagination: {
            total: pagination.total,
            page: pagination.page,
            limit: pagination.limit
        },
        users,
        fetchUsers,
        loading,
        error,

        selectedCashBox,
        setSelectedCashBox,
        isModalOpen,
        setIsModalOpen,

        fetchCashBoxes,
        handleCreate,
        handleOpenToday,
        handleClose,
        handleDelete
    };
};

export default useCashBox;
